import { CloseIcon, ModalContainer, ModalRoot, Title } from './styles';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import Icon from '../Icon';
import { useTheme } from 'styled-components';

interface ModalProps {
  visible: boolean;
  onClose?: () => void;
  title?: string | ReactNode;
  content?: ReactNode;
  contentPadding?: string;
  maxHeight?: string;
  width?: string;
  backgroundColor?: string;
  contentBackgroundColor?: string;
  headerIcon?: JSX.Element;
  ModalFooter?: ReactNode;
  animation?: boolean;
  overflow?: string;
  scrollBarColor?: string;
  height?: string;
}

const RegularModal = ({
  visible,
  onClose,
  content,
  maxHeight,
  width,
  backgroundColor,
  title,
  ModalFooter,
  animation,
  contentBackgroundColor,
  headerIcon,
  contentPadding,
  scrollBarColor,
  overflow,
  height,
}: ModalProps) => {
  const theme = useTheme();
  const [activateAnimation, setActivateAnimation] = useState<boolean>(visible);

  const stopPropagation = (e: Pick<Event, 'stopPropagation'>) => {
    e.stopPropagation();
  };

  useEffect(() => {
    if (animation) setActivateAnimation(visible);
  }, [animation, visible]);

  const onClickClose = useCallback(() => {
    setActivateAnimation(false);
    setTimeout(() => {
      onClose?.();
    }, 600);
  }, [onClose]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && visible) {
        onClickClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [visible, onClickClose]);

  useEffect(() => {
    if (!visible) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [visible]);

  const modalContent = (
    <ModalRoot $mounted={visible} $shouldScroll={!maxHeight}>
      <ModalContainer
        onClick={stopPropagation}
        $width={width}
        $maxHeight={maxHeight}
        $backgroundColor={backgroundColor}
        $activateAnimation={activateAnimation}
        $animation={animation}
        $height={height}
        $contentPadding={contentPadding}
        $overflow={overflow}
      >
        <Title> {title}</Title>
        <CloseIcon onClick={onClickClose}>
          <Icon
            size={28}
            color={theme.color.shadeGrey2}
            indentLess
            name="close"
            $hoverEffect
          />
        </CloseIcon>
        {content}
        <div>{ModalFooter}</div>
      </ModalContainer>
    </ModalRoot>
  );

  return visible ? createPortal(modalContent, document.body) : null;
};

export default RegularModal;
